import { batch, computed, signal } from '@preact/signals-react';
import { t } from 'i18next';
import { RuleCategory } from '../../../common/enums/RuleCategory';
import { TraceType } from '../../../common/enums/TraceType';
import { PROFILE_MAX_NAME_LENGTH } from '../components/ProfileNameInput';
import { ALL_THRESHOLD_RULES, ThresholdRule } from '../models/Threshold';
import { TracerProfile } from '../models/TracerProfile';
import { TracerRule } from '../models/TracerRule';
import { CardList } from './CardList';
import { ProfileStore } from './ProfileStore';

type State = {
  profile: TracerProfile;
  editable: boolean;
};

export class TracerStore extends ProfileStore {
  private static instance: TracerStore | null = null;

  private profile: TracerProfile = TracerProfile.blank();

  // Same fields as in TracerProfile 1:1
  private readonly expiringSoon = signal(<number>0);

  private rsaCards: CardList = CardList.blank(ThresholdRule.RSA);
  private symmetricCards: CardList = CardList.blank(ThresholdRule.SYMMETRIC);
  private ecCards: CardList = CardList.blank(ThresholdRule.EC);
  private certValidityCards: CardList = CardList.blank(ThresholdRule.CERT_VALIDITY);
  private staleKeyCards: CardList = CardList.blank(ThresholdRule.STALE_KEY_202);

  // Additional fields needed for TracerTable to render sucessfully
  private ruleGroups: Map<RuleCategory, TracerRule[]> = new Map();
  private availableTraceTypes: TraceType[] = [];

  private readonly rule203Enabled = computed(() => {
    return !this.disabledRules.value.has(203);
  });

  static getInstance() {
    if (TracerStore.instance === null) {
      TracerStore.instance = new TracerStore();
    }
    return TracerStore.instance;
  }

  setStore(state: State) {
    const { profile, editable } = state;
    this.profile = profile;
    const profileName = profile.getName();
    this.editable = editable;
    this.originalProfileName = profileName;
    this.rsaCards = new CardList(profile.getRsaInfo(), ThresholdRule.RSA);
    this.symmetricCards = new CardList(profile.getSymmetricInfo(), ThresholdRule.SYMMETRIC);
    this.ecCards = new CardList(profile.getEcInfo(), ThresholdRule.EC);
    this.certValidityCards = new CardList(profile.getCertificateValidityInfo(), ThresholdRule.CERT_VALIDITY);
    this.staleKeyCards = new CardList(profile.getStaleKeyInfo(), ThresholdRule.STALE_KEY_202);

    batch(() => {
      this.editableProfileName.value = profileName ? t('createProfileForm.copyOf', { profileName }).substring(0, PROFILE_MAX_NAME_LENGTH) : '';
      this.disabledRules.value = profile.getDisableRulesNumbers();
      this.expiringSoon.value = profile.getExpiringSoon();
    });
  }

  reset() {
    batch(() => {
      this.originalProfileName = '';
      this.editableProfileName.value = '';
      this.disabledRules.value = new Set();
      this.rsaCards = {} as CardList;
      this.symmetricCards = {} as CardList;
      this.ecCards = {} as CardList;
      this.certValidityCards = {} as CardList;
      this.staleKeyCards = {} as CardList;
      this.expiringSoon.value = 0;
      this.editable = false;
      this.ruleGroups = new Map();
      this.availableTraceTypes = [];
    });
  }

  setRuleGroups(groups: Map<RuleCategory, TracerRule[]>) {
    this.ruleGroups = groups;
  }

  setAvailableTraceTypes(types: TraceType[]) {
    this.availableTraceTypes = types;
  }

  isRule203Enabled() {
    return this.rule203Enabled.value;
  }

  getRuleGroups() {
    return this.ruleGroups;
  }

  getAvailableTraceTypes() {
    return this.availableTraceTypes;
  }

  getRsaCards() {
    return this.rsaCards;
  }

  getSymmetricCards() {
    return this.symmetricCards;
  }

  getEcCards() {
    return this.ecCards;
  }

  getCertificateValidityCards() {
    return this.certValidityCards;
  }

  getStaleKeyCards() {
    return this.staleKeyCards;
  }

  setExpiringSoon(value: number) {
    this.expiringSoon.value = value;
    this.validateForm();
  }

  getExpiringSoon() {
    return this.expiringSoon.value;
  }

  private _getCardList(ruleNumber: ThresholdRule): CardList {
    switch (ruleNumber) {
      case ThresholdRule.RSA:
        return this.rsaCards;
      case ThresholdRule.EC:
        return this.ecCards;
      case ThresholdRule.SYMMETRIC:
        return this.symmetricCards;
      case ThresholdRule.CERT_VALIDITY:
        return this.certValidityCards;
      case ThresholdRule.STALE_KEY_202:
      case ThresholdRule.STALE_KEY_203:
        return this.staleKeyCards;
    }
  }

  getCardList(ruleNumber: number): CardList | null {
    if (!(ruleNumber in ThresholdRule)) {
      return null;
    }
    const list = this._getCardList(ruleNumber as ThresholdRule);
    // small exception - do not show cards if a profile is in readonly mode
    // and the API provided no threshold data for it (aka FIPS profile)
    const valuesFromApi = list.areValuesFromApi();
    if (!this.isEditable() && !valuesFromApi) {
      return null;
    }
    return list;
  }

  private resetCardList(ruleNumber: ThresholdRule) {
    switch (ruleNumber) {
      case ThresholdRule.RSA:
        this.rsaCards = new CardList(this.profile.getRsaInfo(), ThresholdRule.RSA);
        break;
      case ThresholdRule.EC:
        this.ecCards = new CardList(this.profile.getEcInfo(), ThresholdRule.EC);
        break;
      case ThresholdRule.SYMMETRIC:
        this.symmetricCards = new CardList(this.profile.getSymmetricInfo(), ThresholdRule.SYMMETRIC);
        break;
      case ThresholdRule.CERT_VALIDITY:
        this.certValidityCards = new CardList(this.profile.getCertificateValidityInfo(), ThresholdRule.CERT_VALIDITY);
        break;
      case ThresholdRule.STALE_KEY_202:
      case ThresholdRule.STALE_KEY_203:
        this.staleKeyCards = new CardList(this.profile.getStaleKeyInfo(), ThresholdRule.STALE_KEY_202);
    }
  }

  updateDisabledRulesState(checked: boolean, ruleNumber: number) {
    const newDisabledRules = new Set(this.disabledRules.value);
    if (checked) {
      newDisabledRules.delete(ruleNumber);
    } else {
      // check if the disabled rule belongs to a ThresholdRule
      // If so, check if there are any errors in the user input
      // If there are errors then reset the card values to the defaults
      // to clear out the errors.
      if (ruleNumber in ThresholdRule) {
        const list = this._getCardList(ruleNumber);
        if (!list.cardsAreValid()) {
          this.resetCardList(ruleNumber);
        }
      }
      newDisabledRules.add(ruleNumber);
    }
    this.disabledRules.value = newDisabledRules;
    this.validateForm();
  }

  validateForm() {
    const thresholdRulesValid = ALL_THRESHOLD_RULES.every((rule) => {
      if (this.disabledRules.value.has(rule)) {
        return true;
      }
      const list = this._getCardList(rule);
      return list.cardsAreValid();
    });

    this.saveEnabled.value = this.editableProfileName.value !== '' && this.expiringSoon.value > 0 && thresholdRulesValid;
  }
}
